// The floor-plan document's core scene + systems — the live instances every
// floor slice works against, owned here (module scope) instead of inside
// floor-mode's install closure so slices can import them directly. This is the
// floor counterpart of core/edit-core: floor-mode (and, as it's carved up, the
// floor/ slices) import these; main adds floorScene.root to the THREE scene at
// install.
//
// Mutation rules are unchanged: edits that should be undoable snapshot via
// commitFloor() (still owned by floor-mode — it needs the model cache + bake
// pipeline to restore). See CLAUDE.md "Undo".

import { M_TO_IN, formatLengthIn, getDisplayUnit } from "../model/units";
import { createFloorScene } from "./floor-scene";
import { createFloorToolRegistry } from "./tool-registry";
import { WallSystem } from "./walls";
import { PlacementSystem } from "./placement";
import { MeasureSystem, rigidGroup } from "./measure";
import { WallLabels } from "./wall-labels";
import { DimensionLabels } from "./dimension-labels";

export const floorScene = createFloorScene();

// The floor gesture-tool registry (Select / Wall / Measure per-frame bodies).
// floor-mode dispatches the active tool's frame; slices register into it.
export const floorTools = createFloorToolRegistry();

export const wallSystem = new WallSystem();
floorScene.root.add(wallSystem.group);
floorScene.root.add(wallSystem.projGroup); // flat footprints when walls hidden
floorScene.root.add(wallSystem.lockGroup); // corner-lock markers

export const placementSystem = new PlacementSystem();
floorScene.root.add(placementSystem.group);

export const measureSystem = new MeasureSystem((m) =>
  formatLengthIn(m * M_TO_IN, getDisplayUnit()),
);
floorScene.root.add(measureSystem.group);
// Measurement visibility is the UNION of two inputs: the Measure tool being
// active (auto-show so you can see what you're measuring) and the user's
// "Measurements" Show-toggle (pin them on regardless of tool). Both feed a
// single recompute so neither stomps the other. Hidden by default (both off).
let measureToolActive = false;
let measurementsPinned = false;
function applyMeasureVisibility(): void {
  measureSystem.setVisible(measureToolActive || measurementsPinned);
}
/** The Measure tool became (in)active — auto-show measurements while it's up. */
export function setMeasureToolActive(on: boolean): void {
  measureToolActive = on;
  applyMeasureVisibility();
}
/** The user's "Measurements" Show-toggle — keep measurements shown regardless
 *  of the active tool. */
export function setMeasurementsPinned(on: boolean): void {
  measurementsPinned = on;
  applyMeasureVisibility();
}
applyMeasureVisibility(); // both off → hidden
// Anchored measure nodes follow their model: resolve each to the model's current
// world position — XZ from the anchored footprint point, plus the model's height
// (its base Y) so the measurement rides up with a raised object instead of
// dropping to the floor. Null if the model was deleted → the node freezes.
measureSystem.setAnchorResolver((a) => {
  const xz = placementSystem.localToWorldXZ(a.id, a.lx, a.lz);
  if (!xz) return null;
  const g = placementSystem.groupOf(a.id);
  return { x: xz.x, z: xz.z, y: g ? g.position.y : 0 };
});

// Persistent length label on each wall (toggled via the View group).
export const wallLabels = new WallLabels((m) =>
  formatLengthIn(m * M_TO_IN, getDisplayUnit()),
);
floorScene.root.add(wallLabels.group);

// Per-axis dimension lines for placed models (W/D, plus H in 3D) and wall
// heights (3D), toggled by "Show dimensions" alongside the wall length pills.
export const extraDims = new DimensionLabels();
floorScene.root.add(extraDims.group);

// Expand a set of placement ids to everything that must translate WITH it: the
// transitive closure over BOTH locked-locked measurement links (a fully-locked
// measurement keeps its measured distance, so the two ends move as a unit) AND
// group membership (a group moves as a unit). Iterated together so a measurement
// to one member of a group drags that whole group, and that group's other links
// drag their groups, and so on. Shared by the placement gizmo and measurement
// value-edits.
export function expandRigidGroup(ids: Iterable<number>): number[] {
  const links = measureSystem.rigidLinks();
  const set = new Set<number>(ids);
  for (;;) {
    const before = set.size;
    for (const id of rigidGroup(links, set)) set.add(id); // rigid-link closure
    for (const id of [...set]) {
      for (const sib of placementSystem.groupSiblings(id)) set.add(sib); // group closure
    }
    if (set.size === before) break; // converged
  }
  return [...set];
}
